import { collectStoredAnnotations, type StoredItem } from '../../../annotations';
import type { ViewerShell } from '../../../shell';
import { findSeries } from '../context';
import { allFindings } from '../findings-overlay';
import { mm } from './dom';
import { boxOfLine } from './geometry';
import type { TrackSource } from './lesions';

/**
 * What "Seguir lesión" can start from on the study: the lines measured on it (the Línea tool, or an
 * accepted "Medir con IA") and the suggested findings with a box that were not discarded. Read only —
 * nothing here changes the annotation store or a finding.
 */

export interface Candidate extends TrackSource {
  title: string;
}

export const SOURCE_TEXT = {
  line: (value: string, slice: number) => `Línea ${value} · corte ${slice}`,
  finding: (label: string, slice: number) => `${label} · corte ${slice}`,
} as const;

const MAX_CANDIDATES = 12;

interface PlaneSize {
  rows?: number;
  columns?: number;
}

/** Rows and columns of the image a stored item is on (from the loaded metadata). */
function imageSize(shell: ViewerShell, item: StoredItem): { rows: number; columns: number } | null {
  const series = findSeries(shell, item.series_uid, item.sop_uid);
  const imageId = series ? shell.imageIdsFor(series)[item.slice - 1] : undefined;
  if (!imageId) return null;
  const plane = window.cornerstone.metaData.get('imagePlaneModule', imageId) as PlaneSize | undefined;
  return plane?.rows && plane.columns ? { rows: plane.rows, columns: plane.columns } : null;
}

function lineLength(item: StoredItem): number | null {
  const result = item.results.find((r) => r[2] === 'length' && r[1] === 'mm');
  const value = result ? Number(result[0]) : NaN;
  return Number.isFinite(value) && value > 0 ? value : null;
}

function lineCandidates(shell: ViewerShell, studyUid: string): Candidate[] {
  const out: Candidate[] = [];
  for (const item of collectStoredAnnotations()) {
    if (item.study_uid !== studyUid || item.type !== 'LINE') continue;
    const size = imageSize(shell, item);
    const box = size ? boxOfLine(item.points, size.columns, size.rows) : null;
    if (!box) continue;
    const longMm = lineLength(item);
    out.push({
      title: SOURCE_TEXT.line(mm(longMm), item.slice),
      location: { series_uid: item.series_uid, instance_uid: item.sop_uid, frame: item.frame, index: item.slice - 1, box },
      longMm,
      shortMm: null,
    });
  }
  return out;
}

function findingCandidates(shell: ViewerShell, studyUid: string): Candidate[] {
  const out: Candidate[] = [];
  for (const f of allFindings()) {
    const loc = f.location;
    if (f.status === 'rejected' || !loc?.box || !loc.instance_uid || loc.index === null) continue;
    if (findSeries(shell, loc.series_uid, loc.instance_uid)?.studyInstanceUID !== studyUid) continue;
    const label = f.correction ?? f.label;
    out.push({
      title: SOURCE_TEXT.finding(label, loc.index + 1),
      label,
      location: { series_uid: loc.series_uid, instance_uid: loc.instance_uid, frame: loc.frame, index: loc.index, box: loc.box },
      longMm: null,
      shortMm: null,
    });
  }
  return out;
}

export function trackCandidates(shell: ViewerShell, studyUid: string): Candidate[] {
  return [...lineCandidates(shell, studyUid), ...findingCandidates(shell, studyUid)].slice(0, MAX_CANDIDATES);
}
